/**
 * Page shell for a nav section whose subpages render as horizontal tabs
 * (Resale Pricing, Catalog, Storefront, Repairs, Settings) instead of separate
 * sidebar entries. Each tab is still its own route, so deep links and the
 * resources' create/edit/history subroutes keep working.
 */
import { Box, Tab, Tabs, Typography } from '@mui/material';
import { AccessDenied, Loading, useCanAccessResources } from 'react-admin';
import { Link, Navigate } from 'react-router-dom';
import { TroveOnlyBadge, useIsTroveUser } from '@src/troveOnly';

export interface SectionTab<T extends string = string> {
  value: T;
  label: string;
  path: string;
  /** When set, the tab only shows if the user can access this resource. */
  resource?: string;
  /** Hides the tab outright (e.g. behind a feature flag). */
  hidden?: boolean;
  /** Shows the tab only to Trove employees, marked with a "Trove only" badge. */
  troveOnly?: boolean;
}

/**
 * Filters a section's tabs down to the ones the current user can see.
 *
 * Callers must branch on all three results, because `visibleTabs` is empty in
 * two very different situations: while `isPending` (permissions haven't
 * resolved, so `canAccess` is still undefined) and when `error` is set (the
 * check failed). Treating either as "denied" tells a user they lack access
 * they may well have. Sections with `troveOnly` tabs also stay pending until the
 * user's identity resolves, so a Trove employee deep-linking to one isn't denied.
 */
export const useVisibleTabs = <T extends string>(tabs: SectionTab<T>[]) => {
  const { isTroveUser, isPending: isIdentityPending } = useIsTroveUser();
  const isHidden = ({ hidden, troveOnly }: SectionTab<T>) => !!hidden || (!!troveOnly && !isTroveUser);
  const resources = tabs.flatMap(tab => (tab.resource && !isHidden(tab) ? [tab.resource] : []));
  const { canAccess, isPending, error } = useCanAccessResources({ action: 'list', resources });
  const visibleTabs = tabs.filter(
    tab => !isHidden(tab) && (!tab.resource || !!canAccess?.[tab.resource])
  );
  const waitsOnIdentity = isIdentityPending && tabs.some(({ troveOnly, hidden }) => troveOnly && !hidden);
  return { visibleTabs, isPending: isPending || waitsOnIdentity, error };
};

interface SectionTabShellProps<T extends string> {
  title: string;
  description?: string;
  tabs: SectionTab<T>[];
  tab: T;
  /** Buttons rendered at the top right of the page header. */
  actions?: React.ReactNode;
  children: React.ReactNode;
}

const SectionTabShell = <T extends string>({
  title,
  description,
  tabs,
  tab,
  actions,
  children,
}: SectionTabShellProps<T>) => {
  const { visibleTabs, isPending, error } = useVisibleTabs(tabs);
  const isCurrentTabVisible = visibleTabs.some(({ value }) => value === tab);

  // Permissions resolved and this tab is not among them — the user reached a
  // page they can't have (a stale bookmark, usually). Deny here rather than
  // painting the header and a selected tab first and letting the route's own
  // guard bounce them a frame later.
  if (!isPending && !error && !isCurrentTabVisible) return <AccessDenied />;

  // Still pending (or the check failed): `canAccess` is undefined, so every
  // gated tab was filtered out. Keep the current one mounted so MUI never sees
  // a `value` with no matching Tab.
  const shownTabs = isCurrentTabVisible ?
    visibleTabs :
    [...visibleTabs, ...tabs.filter(({ value }) => value === tab)];

  return (
    <Box>
      <Box sx={{ mb: 1.5, display: 'flex', alignItems: 'flex-start', gap: 2 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h5" sx={{ fontWeight: 600, fontSize: 22 }}>
            {title}
          </Typography>
          {description && (
            <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.25 }}>
              {description}
            </Typography>
          )}
        </Box>
        {actions && <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>{actions}</Box>}
      </Box>
      <Tabs
        value={tab}
        // Scroll rather than widen the page when the tabs outgrow a narrow viewport.
        variant="scrollable"
        scrollButtons={false}
        sx={{
          borderBottom: '1px solid',
          borderColor: 'divider',
          minHeight: 40,
          '& .MuiTab-root': { minHeight: 40, textTransform: 'none', fontWeight: 500 },
        }}
      >
        {shownTabs.map(({ value, label, path, troveOnly }) => (
          <Tab
            key={value}
            value={value}
            label={troveOnly ?
              <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                {label}
                <TroveOnlyBadge />
              </Box> :
              label}
            component={Link}
            to={path}
          />
        ))}
      </Tabs>
      <Box sx={{ pt: 1 }}>{children}</Box>
    </Box>
  );
};

/**
 * Mounted at a section root (e.g. /settings): sends the user to the first tab
 * they can see. Shows AccessDenied only when the permission check succeeded and
 * genuinely returned nothing — a failed check goes to react-admin's
 * authentication-error route instead, matching what <CanAccess> does.
 */
export const SectionIndexRedirect = ({ tabs }: { tabs: SectionTab[] }) => {
  const { visibleTabs, isPending, error } = useVisibleTabs(tabs);
  if (isPending) return <Loading />;
  if (error) {
    console.error('SectionIndexRedirect: permission check failed', {
      tabs: tabs.map(({ value }) => value),
      error,
    });
    return <Navigate to="/authentication-error" replace />;
  }
  if (!visibleTabs.length) return <AccessDenied />;
  return <Navigate to={visibleTabs[0].path} replace />;
};

export default SectionTabShell;
